/**
 * RAM — HoldOrStop action.
 *
 * Places a hold or stop-payment on an account via AcctMod. The caller supplies
 * AcctId + AcctType on the target. Operators enter a reason code (free-text
 * until we pin the Core Director hold reason list — TODO(collette)).
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { asIso8601 } from '@retail-ops/shared-types';
import type {
  AcctModRequest,
  AcctType,
  SessionEventPayload,
} from '@retail-ops/shared-types';
import type { RamTarget } from '../types';

export interface HoldOrStopProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

type HoldMode = 'hold' | 'stop';

export default function HoldOrStop({
  target,
  onDone,
  onCancel,
}: HoldOrStopProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();

  const [mode, setMode] = useState<HoldMode>('hold');
  const [reasonCode, setReasonCode] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<boolean>(false);

  const canSave =
    !saving &&
    !!target.acctId &&
    !!target.acctType &&
    reasonCode.trim().length > 0;

  const handleSave = async (): Promise<void> => {
    if (!canSave || !target.acctId || !target.acctType) return;
    setSaving(true);
    setError(null);
    try {
      const req: AcctModRequest = {
        AcctId: target.acctId,
        AcctType: target.acctType as AcctType,
        Fields: {
          [mode === 'hold' ? 'HoldCode' : 'StopPayCode']: reasonCode,
          HoldNotes: notes || undefined,
        },
      };
      const res = await client.accounts.acctMod(req);

      const payload: SessionEventPayload = {
        eventType: 'account-modified',
        eventCategory: 'cif-account',
        severity: 'info',
        actor: user?.upn ?? 'fsr',
        timestamp: asIso8601(new Date().toISOString()),
        payloadJson: JSON.stringify({
          action: mode === 'hold' ? 'hold' : 'stop',
          acctId: target.acctId,
          acctType: target.acctType,
          reasonCode,
        }),
      };
      client.retail.events.write(payload).catch(() => undefined);

      if (!res.success) {
        setError(res.error ?? 'AcctMod returned a non-success result.');
        setSaving(false);
        return;
      }
      setDone(true);
      setSaving(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to apply hold.');
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          {mode === 'hold' ? 'Hold placed' : 'Stop payment placed'} on account{' '}
          {target.acctId}.
        </Banner>
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Hold or stop payment</h2>
        {target.acctId ? (
          <Pill variant="info">Acct {target.acctId}</Pill>
        ) : (
          <Pill variant="warn">Select an account</Pill>
        )}
      </div>

      <div className="ram-wizard__fields">
        <label className="ram-search__label">
          Mode
          <select
            className="ram-input"
            value={mode}
            onChange={(e) => setMode(e.target.value as HoldMode)}
          >
            <option value="hold">Place hold</option>
            <option value="stop">Stop payment</option>
          </select>
        </label>
        <label className="ram-search__label">
          Reason code
          <input
            className="ram-input"
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value.toUpperCase())}
            maxLength={4}
            placeholder="e.g. LG, FR"
          />
        </label>
        <label
          className="ram-search__label"
          style={{ gridColumn: '1 / -1' }}
        >
          Notes
          <textarea
            className="ram-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            rows={3}
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {saving ? 'Applying…' : mode === 'hold' ? 'Place hold' : 'Place stop pay'}
        </Button>
      </div>
    </div>
  );
}
